import {PullRequestDiff, type PullRequestDiffProps} from './PullRequestDiff'

import {PullRequestDiffEntryErrorFallback} from './PullRequestDiffEntryErrorFallback'
import {memo} from 'react'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {useDiffEntry, useDiffSummary, useProgressiveDiffEntries, useSelectedPathDigest} from '../stores/selectors'
import {DiffEntryLoadingSkeleton} from './progressive-diff/DiffEntryLoadingSkeleton'
import {DiffFileHeader} from '@github-ui/diff-file-header'
import {noop} from '@github-ui/noop'
import styles from '@github-ui/diff-lines/Diff.module.css'
import {clsx} from 'clsx'
import {ProgressiveLoadingStatus} from '../types/progressive-diff-types'
import type {PullRequestFileTreeDiff} from '../page-data/payloads/file-tree'
import {FilesChangedFilterBlankSlate} from './FilesChangedFilterBlankSlate'

export type SingleFileModePullRequestDiffEntryProps = {
  diffEntryPathDigest: string
  filteredDiffSummaries: PullRequestFileTreeDiff[]
} & Pick<
  PullRequestDiffProps,
  | 'basePath'
  | 'commentingEnabled'
  | 'commentBoxSubject'
  | 'contextLinesURL'
  | 'currentUser'
  | 'diffManuallyExpanded'
  | 'headBranchName'
  | 'headRepoName'
  | 'headRepoOwner'
  | 'isSelected'
  | 'pullRequestGlobalRelayId'
  | 'pullRequestState'
  | 'repository'
>

export const SingleFileModePullRequestDiffEntry = memo(function SingleFileModePullRequestDiffEntry({
  diffEntryPathDigest,
  basePath,
  commentBoxSubject,
  commentingEnabled,
  contextLinesURL,
  currentUser,
  diffManuallyExpanded,
  filteredDiffSummaries,
  headBranchName,
  headRepoName,
  headRepoOwner,
  pullRequestGlobalRelayId,
  pullRequestState,
  repository,
}: SingleFileModePullRequestDiffEntryProps) {
  const selectedPathDigest = useSelectedPathDigest() || diffEntryPathDigest
  const progressiveDiffEntry = useProgressiveDiffEntries().find(entry => entry.pathDigest === selectedPathDigest)
  const diffSummary = useDiffSummary(progressiveDiffEntry?.path || '')
  const isSelected = progressiveDiffEntry?.pathDigest === selectedPathDigest
  const diffEntryPath = progressiveDiffEntry?.path || ''
  const currentDiffEntry = useDiffEntry(diffEntryPath)

  const matchesFilter = filteredDiffSummaries.some(ds => ds.path === diffEntryPath)
  if (!matchesFilter) {
    return <FilesChangedFilterBlankSlate />
  }
  if (progressiveDiffEntry && progressiveDiffEntry.loadingStatus !== ProgressiveLoadingStatus.Loaded) {
    const approximateLineCount = 5

    const loadingSkeleton = (
      <DiffEntryLoadingSkeleton
        ariaLabel={`Loading ${diffEntryPath}`}
        testId={`eager-load-${diffEntryPath}`}
        id={`diff-${selectedPathDigest}`}
        approximateLineCount={approximateLineCount}
      />
    )

    return (
      <div
        role="region"
        id={`diff-${selectedPathDigest}`}
        className={clsx(styles.diffTargetable, styles.diff)}
        data-targeted={isSelected ?? false}
        key={`${selectedPathDigest}_${diffEntryPath}`}
      >
        <div className={styles.diffHeaderWrapper}>
          <DiffFileHeader
            areLinesExpanded={false}
            canExpandOrCollapseLines={false}
            fileLinkHref={`#diff-${selectedPathDigest}`}
            canToggleRichDiff={false}
            linesAdded={diffSummary?.linesAdded ?? 0}
            linesChanged={diffSummary?.linesChanged ?? 0}
            linesDeleted={diffSummary?.linesDeleted ?? 0}
            newPath={diffEntryPath}
            patchStatus={''}
            path={diffEntryPath}
            onToggleExpandAllLines={noop}
            onToggleFileCollapsed={noop}
            onToggleDiffDisplay={noop}
          />
        </div>
        {loadingSkeleton}
      </div>
    )
  }
  if (!currentDiffEntry) {
    return (
      <PullRequestDiffEntryErrorFallback
        linesAdded={0}
        linesChanged={0}
        linesDeleted={0}
        path={diffEntryPath}
        pathDigest={diffEntryPathDigest}
        newTreeEntry={undefined}
        oldTreeEntry={undefined}
        status={'MODIFIED'}
      />
    )
  }

  return (
    <ErrorBoundary fallback={<PullRequestDiffEntryErrorFallback {...currentDiffEntry} />}>
      <PullRequestDiff
        basePath={basePath}
        changeType={currentDiffEntry.status}
        commentBoxSubject={commentBoxSubject}
        commentingEnabled={commentingEnabled}
        contextLinesURL={contextLinesURL}
        currentUser={currentUser}
        diffLines={currentDiffEntry.diffLines}
        diffManuallyExpanded={diffManuallyExpanded}
        diffSize={currentDiffEntry.diffSize}
        headBranchName={headBranchName}
        headRepoName={headRepoName}
        headRepoOwner={headRepoOwner}
        helpUrl={currentDiffEntry.helpUrl}
        isBinary={currentDiffEntry.isBinary}
        isSelected={isSelected ?? false}
        isTooBig={currentDiffEntry.isTooBig}
        linesAdded={currentDiffEntry.linesAdded}
        linesChanged={currentDiffEntry.linesChanged}
        linesDeleted={currentDiffEntry.linesDeleted}
        newCommitOid={currentDiffEntry.newCommitOid}
        newTreeEntry={currentDiffEntry.newTreeEntry}
        objectId={currentDiffEntry.objectId}
        oldCommitOid={currentDiffEntry.oldCommitOid}
        oldTreeEntry={currentDiffEntry.oldTreeEntry}
        path={currentDiffEntry.path}
        pathDigest={currentDiffEntry.pathDigest}
        pullRequestGlobalRelayId={pullRequestGlobalRelayId}
        pullRequestState={pullRequestState}
        repository={repository}
        reviewed={currentDiffEntry.reviewed ?? false}
        richDiff={currentDiffEntry.richDiff}
        status={currentDiffEntry.status}
        submodule={currentDiffEntry.submodule}
        truncatedReason={currentDiffEntry.truncatedReason}
      />
    </ErrorBoundary>
  )
})

try{ SingleFileModePullRequestDiffEntry.displayName ||= 'SingleFileModePullRequestDiffEntry' } catch {}